/**
 * Your agency's people on Momentum AMP — the human half of "the team"
 * (the Digital Team agents are the other half).
 *
 * Roster: the agency's AMS users (/api/account/agentlist, the same list
 * Profile → Agents and command sharing use). Each has two ids:
 *   - `id`     — the AMS agent id; command sharing (sharedAgentIds) uses it.
 *   - `userId` — the Momentum AMP user id; a command's owner / assigneeId use
 *                it. Empty when the person has never signed in to Momentum
 *                AMP, so they can't be handed work yet.
 *
 * Membership: an agency admin chooses who's on the team in the Admin
 * Dashboard (Team access). Saved per agency on the server
 * (GET/PUT /api/account/team-members → agency_team_member).
 *
 * Sidebar: each person then picks which enabled teammates show under Team
 * in their own sidebar ("Add people"). Nothing picked yet means all of them.
 * [NEEDS VERIFICATION — backend]
 */
import { useEffect, useState } from 'react';
import { fetchAgencyAgents, type AgencyAgent } from './kanbanAIService';

const CHANGE_EVENT = 'ig-team-members-changed';
const SIDEBAR_KEY = 'ig-team-sidebar-picks';

export interface TeamMember {
  /** AMS agent id (sharing). */
  id: string;
  /** userId from the agent list (assignment); '' if they've never signed in. */
  userId: string;
  /**
   * UserInformation.Id — the id MyAMP takes as user_id (e.g. 42). Only set by
   * fetchAgencyUsers; '' on the AMS roster.
   */
  ampUserId: string;
  name: string;
  email: string;
  initials: string;
}

export function isAgencyAdmin(): boolean {
  return localStorage.getItem('isAgencyOwner') === 'true';
}

function toMember(a: AgencyAgent): TeamMember {
  const name = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.email || 'Teammate';
  const initials = [a.firstName, a.lastName].map(s => (s || '').trim().charAt(0)).join('').toUpperCase()
    || name.charAt(0).toUpperCase();
  return { id: a.id, userId: (a.userId || '').trim(), ampUserId: '', name, email: a.email || '', initials };
}

/** True for the signed-in user's own roster row. */
export function isSelf(m: TeamMember): boolean {
  const selfUserId = (localStorage.getItem('userId') || '').trim();
  const selfEmail = (localStorage.getItem('userEmail') || '').trim().toLowerCase();
  return (!!selfUserId && (m.userId === selfUserId || m.ampUserId === selfUserId))
    || (!!selfEmail && m.email.toLowerCase() === selfEmail);
}

/** Called when team membership or your sidebar picks change. */
export function subscribeToTeamChanges(fn: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, fn);
  return () => window.removeEventListener(CHANGE_EVENT, fn);
}

/** Which enabled teammates you've put in your sidebar; null = all of them. */
export function getSidebarPicks(): string[] | null {
  try {
    const raw = localStorage.getItem(SIDEBAR_KEY);
    return raw ? (JSON.parse(raw) as string[]) : null;
  } catch { return null; }
}

/** Add or remove someone from your sidebar. `all` is everyone you could pick. */
export function setSidebarPick(id: string, shown: boolean, all: string[]): void {
  const current = getSidebarPicks() ?? all;
  const next = shown ? Array.from(new Set([...current, id])) : current.filter(x => x !== id);
  try { localStorage.setItem(SIDEBAR_KEY, JSON.stringify(next)); } catch { /* storage blocked */ }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

let rosterPromise: Promise<TeamMember[]> | null = null;
/** The full AMS roster (cached for the session; a failed load retries). */
export function fetchRoster(force = false): Promise<TeamMember[]> {
  if (!rosterPromise || force) {
    rosterPromise = fetchAgencyAgents()
      .then(list => list.map(toMember))
      .catch(err => { rosterPromise = null; throw err; });
  }
  return rosterPromise;
}

// ── Team membership (server) ──────────────────────────────────────────────

/** Same base as agency-users, so a local API override covers both calls. */
const accountApiBase = () => process.env.REACT_APP_AgencyUsers_Url || process.env.REACT_APP_Auth_Url || '';

let teamMembers: TeamMember[] | null = null;
let teamMembersPromise: Promise<TeamMember[]> | null = null;

function teamMemberFromApi(u: any): TeamMember {
  const first = String(u.firstName ?? '').trim();
  const last = String(u.lastName ?? '').trim();
  const email = String(u.email ?? '').trim();
  const name = `${first} ${last}`.trim() || email || 'Teammate';
  const initials = [first, last].map(x => x.charAt(0)).join('').toUpperCase() || name.charAt(0).toUpperCase();
  return {
    id: String(u.agentId ?? ''),
    userId: String(u.nowcertsUserId ?? ''),
    // Saved independently of registration; the Admin Dashboard matches registered
    // people (and their Momentum AMP id) from agency-users when it needs it.
    ampUserId: '',
    name,
    email,
    initials,
  };
}

/**
 * Who the agency admin has put on the team (GET /api/account/team-members), as
 * saved — registered in Momentum AMP or not. `id` is the AMS agent id, the same
 * id the agentlist roster uses. Cached; a failed load retries.
 */
export function fetchTeamMembers(force = false): Promise<TeamMember[]> {
  if (!teamMembersPromise || force) {
    teamMembersPromise = fetch(`${accountApiBase()}/api/account/team-members`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
    })
      .then(async res => {
        if (!res.ok) throw new Error(`Couldn't load your team (${res.status}).`);
        const json = await res.json();
        const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
        teamMembers = list.map(teamMemberFromApi).filter(m => m.id);
        return teamMembers;
      })
      .catch(err => { teamMembersPromise = null; throw err; });
  }
  return teamMembersPromise;
}

/** Switch someone on or off for the team (agency admins). Saves, then updates the cached list. */
export async function saveTeamMember(member: TeamMember, enabled: boolean): Promise<void> {
  const [firstName, ...rest] = member.name.split(' ');
  const res = await fetch(`${accountApiBase()}/api/account/team-members`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
    body: JSON.stringify({
      agentId: member.id,
      nowcertsUserId: member.userId || null,
      firstName: firstName || null,
      lastName: rest.join(' ') || null,
      email: member.email || null,
      enabled,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message || body?.Message || `Couldn't save that change (${res.status}).`);
  }
  // Reload the saved list. If only the reload fails, the change was still saved: show it locally.
  try {
    await fetchTeamMembers(true);
  } catch {
    const current = teamMembers ?? [];
    teamMembers = enabled ? [...current.filter(m => m.id !== member.id), member] : current.filter(m => m.id !== member.id);
    teamMembersPromise = Promise.resolve(teamMembers);
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

let agencyUsersPromise: Promise<TeamMember[]> | null = null;
/**
 * Your agency's Momentum AMP users (GET /api/account/agency-users — UserInformation
 * for your company, so only people registered in Momentum AMP). Each has ampUserId.
 * `id` is their NowCerts agent id when known, so Team access switches still line up
 * with the AMS roster. Cached for the session; a failed load retries.
 * REACT_APP_AgencyUsers_Url (optional, e.g. .env.local) points just this call at
 * another API, such as a local build.
 */
export function fetchAgencyUsers(force = false): Promise<TeamMember[]> {
  if (!agencyUsersPromise || force) {
    agencyUsersPromise = fetch(`${accountApiBase()}/api/account/agency-users`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` },
    })
      .then(async res => {
        if (!res.ok) throw new Error(`Couldn't load your agency's users (${res.status}).`);
        const json = await res.json();
        const list: any[] = Array.isArray(json) ? json : (json?.data ?? []);
        return list.map(u => {
          const first = String(u.firstName ?? u.FirstName ?? '').trim();
          const last = String(u.lastName ?? u.LastName ?? '').trim();
          const email = String(u.workEmail ?? u.WorkEmail ?? u.username ?? u.Username ?? '').trim();
          const ampUserId = String(u.id ?? u.Id ?? '');
          const name = `${first} ${last}`.trim() || email || 'Teammate';
          const initials = [first, last].map(s => s.charAt(0)).join('').toUpperCase() || name.charAt(0).toUpperCase();
          return {
            id: String(u.nowCertsAgentId ?? u.NowCertsAgentId ?? '') || `amp-${ampUserId}`,
            userId: String(u.nowcertsUserId ?? u.NowcertsUserId ?? ''),
            ampUserId,
            name,
            email,
            initials,
          };
        });
      })
      .catch(err => { agencyUsersPromise = null; throw err; });
  }
  return agencyUsersPromise;
}

/** Enabled teammates other than you (`members`) and the ones you've put
 *  in your sidebar (`shown`), kept in sync with the Admin Dashboard. */
export function useTeamMembers(): { members: TeamMember[]; shown: TeamMember[]; loading: boolean } {
  const [team, setTeam] = useState<TeamMember[] | null>(null);
  const [picks, setPicks] = useState(getSidebarPicks);
  useEffect(() => {
    let cancelled = false;
    const load = () => fetchTeamMembers().then(r => { if (!cancelled) setTeam(r); }).catch(() => { if (!cancelled) setTeam([]); });
    load();
    const onChange = () => { load(); setPicks(getSidebarPicks()); };
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => { cancelled = true; window.removeEventListener(CHANGE_EVENT, onChange); };
  }, []);
  const roster = team;
  const members = (roster ?? []).filter(m => !isSelf(m));
  const shown = picks ? members.filter(m => picks.includes(m.id)) : members;
  return { members, shown, loading: roster === null };
}
